import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FeedRefreshBanner from './FeedRefreshBanner';
import { useShowcaseFeedLoader } from '../hooks/useShowcaseFeedLoader';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  fetchShowcaseLeaderboard, 
  clearError,
  selectShowcaseLeaderboard,
  selectLeaderboardShowcaseLoading,
  selectLeaderboardShowcaseError,
  selectLeaderboardShowcasePagination
} from '../redux/slices/leaderboardShowcaseSlice';
import { 
  Trophy, 
  ArrowRight,
  Medal,
  Star,
  Crown,
  Zap,
  Target,
  Users
} from 'lucide-react';
import ShowcaseNavigation from './ShowcaseNavigation';

const LeaderboardCard = ({ user, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getLevelIcon = (level) => {
    switch (level) {
      case 'LEGEND':
        return <Crown className="w-4 h-4 text-yellow-400" />;
      case 'ELITE':
        return <Star className="w-4 h-4 text-purple-400" />;
      case 'PRO':
        return <Zap className="w-4 h-4 text-blue-400" />;
      case 'SEMI_PRO':
        return <Target className="w-4 h-4 text-green-400" />;
      case 'AMATEUR':
        return <Medal className="w-4 h-4 text-orange-400" />;
      default:
        return <Users className="w-4 h-4 text-gray-400" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'LEGEND':
        return 'from-yellow-500/20 to-yellow-600/20 border-yellow-500/30 text-yellow-400';
      case 'ELITE':
        return 'from-purple-500/20 to-purple-600/20 border-purple-500/30 text-purple-400';
      case 'PRO':
        return 'from-blue-500/20 to-blue-600/20 border-blue-500/30 text-blue-400';
      case 'SEMI_PRO':
        return 'from-green-500/20 to-green-600/20 border-green-500/30 text-green-400';
      case 'AMATEUR':
        return 'from-orange-500/20 to-orange-600/20 border-orange-500/30 text-orange-400';
      default:
        return 'from-gray-500/20 to-gray-600/20 border-gray-500/30 text-gray-400';
    }
  };

  const getPositionIcon = (position) => {
    switch (position) {
      case 1:
        return <Trophy className="w-5 h-5 text-yellow-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-300" />;
      case 3:
        return <Medal className="w-5 h-5 text-orange-400" />;
      default:
        return <span className="text-lg font-bold text-white">#{position}</span>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="relative group"
    >
      <div 
        className="relative bg-gradient-to-br from-gray-900/80 to-black/90 backdrop-blur-sm 
                   border border-gray-700/50 rounded-2xl p-6 h-full
                   hover:border-purple-500/50 transition-all duration-300
                   hover:shadow-2xl hover:shadow-purple-500/10"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Position and Name */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8">
              {getPositionIcon(user.position)}
            </div>
            <div>
              <h3 className="text-lg font-bold text-white truncate max-w-[200px]">
                {user.name || 'Anonymous'}
              </h3>
              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border bg-gradient-to-r ${getLevelColor(user.userLevel)}`}>
                {getLevelIcon(user.userLevel)}
                <span>{user.userLevel || 'BEGINNER'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scores */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span className="text-xs text-gray-500">Weekly Score</span>
            </div>
            <p className="text-xl font-bold text-white">
              {user.currentPeriodScore || 0}
            </p>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-xs text-gray-500">All-Time Score</span>
            </div>
            <p className="text-xl font-bold text-white">
              {user.allTimeScores || 0}
            </p>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="mb-6">
          <p className="text-xs text-gray-500 mb-3">Score Breakdown</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-400">Bet House:</span>
              <span className="text-white">{user.houseScores || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Bettings:</span>
              <span className="text-white">{user.bettingsMadeScores || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Predictions:</span>
              <span className="text-white">{user.predictionsScores || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">P2P:</span>
              <span className="text-white">{user.p2pScores || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Casino:</span>
              <span className="text-white">{user.casinoScore || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Polymarket:</span>
              <span className="text-white">{user.polymarketScore || 0}</span>
            </div>
          </div>
        </div>

        {/* Call to Action Overlay */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-2xl flex items-center justify-center"
            >
              <div className="flex gap-3">
                <motion.button
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 hover:from-blue-700 hover:to-blue-800 transition-all duration-300"
                >
                  <span>iOS</span>
                  <ArrowRight className="w-3 h-3" />
                </motion.button>
                <motion.button
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 hover:from-green-700 hover:to-green-800 transition-all duration-300"
                >
                  <span>Android</span>
                  <ArrowRight className="w-3 h-3" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gradient Border Effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    </motion.div>
  );
};

const LeaderboardShowcase = () => {
  const dispatch = useDispatch();
  const leaderboard = useSelector(selectShowcaseLeaderboard);
  const isLoading = useSelector(selectLeaderboardShowcaseLoading);
  const error = useSelector(selectLeaderboardShowcaseError);
  const pagination = useSelector(selectLeaderboardShowcasePagination);
  const { load, isUpdating, refreshFailed } = useShowcaseFeedLoader({
    fetchAction: fetchShowcaseLeaderboard,
    items: leaderboard,
    pageSize: 10,
  });

  if (error && leaderboard.length === 0) {
    return (
      <>
        <ShowcaseNavigation title="Leaderboard Showcase" icon={Trophy} />
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 pb-20">
          <div className="container mx-auto px-4 text-center">
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-6 max-w-md mx-auto">
              <p className="text-red-400 mb-4">Failed to load leaderboard data</p>
              <button
                onClick={() => {
                  dispatch(clearError());
                  dispatch(fetchShowcaseLeaderboard({ page: 1, limit: 10, force: true }));
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ShowcaseNavigation title="Leaderboard Showcase" icon={Trophy} />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 pb-20">
        <div className="container mx-auto px-4">
          <FeedRefreshBanner
            isUpdating={isUpdating}
            refreshFailed={refreshFailed}
            onRetry={() => load({ force: true })}
          />

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Top 10 <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Players</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Weekly competition • {pagination.totalUsers || 0} total players
            </p>
          </div>

          {/* Loading State */}
          {isLoading && leaderboard.length === 0 && (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          )}

          {/* Leaderboard Grid */}
          {leaderboard.length > 0 && (
            <>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12"
              >
                <AnimatePresence>
                  {leaderboard.map((user, index) => (
                    <LeaderboardCard key={user._id} user={user} index={index} />
                  ))}
                </AnimatePresence>
              </motion.div>
            </>
          )}

          {/* Empty State */}
          {!isLoading && leaderboard.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">No players found</h3>
              <p className="text-gray-400 mb-6">
                No leaderboard data available at the moment.
              </p>
            </motion.div>
          )}

        </div>
      </div>
    </>
  );
};

export default LeaderboardShowcase;
